import { Params } from '@angular/router';
import { Bounds, Marker, Polygon, Polyline, Svg } from 'src/app/models/map';
import {
  TimelineChartInput,
  TimelineChartInputV2,
} from '../../timeline-chart/models/timeline-chart-input.interface';

/**
 * The whole `pozi-google-maps` payload.
 *
 * `TInfo` is the feature's own `infoWindowData` shape, threaded down to `Marker<TInfo>` so the
 * generic C3 introduced can actually reach a consumer — before B15 nothing could instantiate it
 * and `drawings.markers` was `Marker<unknown>[]` everywhere (TODO-68). It defaults to `unknown`,
 * so the payloads with no info window (tiles, route/run maps) say nothing and change nothing;
 * a feature that puts a payload in `infoWindowData` names its type here, and
 * `MarkerInfoWindowTemplateDirective` carries that type into its `<ng-template>`.
 */
export interface GoogleMapsInput<TInfo = unknown> {
  controls?: GoogleMapsControlInput;
  drawings?: GoogleMapsDrawingsInput<TInfo>;
  routerLink?: string | string[];
  queryParams?: Params | null;
  grayscale?: boolean;
  timeline?: TimelineChartInput | TimelineChartInputV2;
  mapTypeId?: MapTypeId;
  title?: string;
}

export interface GoogleMapsControlInput {
  zoom?: boolean;
  fullScreen?: boolean;
  mapType?: boolean;
  layers?: GoogleMapsLayerOption[];
  routeSpeed?: boolean;
  scale?: boolean;
}

export interface GoogleMapsLayerOption {
  key: string;
  label: string;
  isSelected: boolean;
}

export interface GoogleMapsDrawingsInput<TInfo = unknown> {
  markers?: Marker<TInfo>[];
  polygons?: Polygon[];
  polylines?: Polyline[];
  svgs?: Svg[];
  bounds?: Bounds;
}

export interface GoogleMapsScaleControlInput {
  zoom?: number;
  lat?: number;
}

export type MapTypeId = 'roadmap' | 'hybrid' | 'satellite';

export type GoogleMapsControlType =
  keyof GoogleMapsControlInput | 'title' | 'routerLink' | 'minimizedTimeline';

export enum GoogleMapsControlPosition {
  TOP_LEFT = 'top-left',
  TOP_RIGHT = 'top-right',
  BOTTOM_LEFT = 'bottom-left',
  BOTTOM_RIGHT = 'bottom-right',
}

export const NON_INTERACTIVE_GOOGLE_MAPS_OPTIONS: google.maps.MapOptions = {
  disableDefaultUI: true,
  scaleControl: false,
  draggable: false,
  zoomControl: false,
  scrollwheel: false,
  disableDoubleClickZoom: true,
  clickableIcons: false,
  keyboardShortcuts: false,
  maxZoom: 16,
};
